// CR072: Forum Deals Panel - Unified Community Signals Management
// Refactored from accordion to card-based collapsible sections for Admin Console

import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Loader2, Play, RefreshCw, Settings, List, Settings2, Layers, 
  Save, AlertCircle, CheckCircle2, ChevronRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CommunitySignalsSettings } from "./CommunitySignalsSettings";
import { CommunitySignalsList } from "./CommunitySignalsList";
import { CommunityCandidateSummary, CommunitySignalBuilder, SignalScoreConfigPanel, PurgeSignalsDialog } from "./community-signals";
import { useCommunitySignals } from "@/hooks/useCommunitySignals";
import { useCommunityCandidateConfig } from "@/hooks/useCommunityCandidateConfig";
import type { CommunitySourceScope, CommunitySignal } from "@/lib/community-signals/types";

const SCOPE_OPTIONS: { value: CommunitySourceScope; label: string }[] = [
  { value: 'ALL', label: 'All Community Sources' },
  { value: 'REDFLAGDEALS', label: 'RedFlagDeals' },
  { value: 'REDDIT', label: 'Reddit' },
  { value: 'FORUMS', label: 'Forums / Other' },
];

interface CollapsibleSectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection = ({ 
  title, 
  description, 
  icon, 
  badge,
  children, 
  defaultOpen = false 
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10 text-primary">
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{title}</CardTitle>
                  {badge}
                </div>
                <CardDescription className="text-sm">{description}</CardDescription>
              </div>
              <ChevronRight className={cn(
                "h-5 w-5 text-muted-foreground transition-transform duration-200 shrink-0",
                isOpen && "rotate-90"
              )} />
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export function CommunitySignalsUnifiedPanel() {
  const [selectedScope, setSelectedScope] = useState<CommunitySourceScope>('ALL');
  const [localThreshold, setLocalThreshold] = useState<number | null>(null);
  const [localSignals, setLocalSignals] = useState<CommunitySignal[] | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Ingestion hook
  const {
    settings,
    isLoadingSettings,
    sources,
    isLoadingSources,
    signals,
    isLoadingSignals,
    runIngestion,
    isRunning,
    refetchAll
  } = useCommunitySignals();

  // Candidate scoring config hook
  const {
    config,
    allConfigs,
    isLoading: isLoadingConfig,
    isSaving,
    updateSignals,
    updateThreshold,
    toggleEnabled,
  } = useCommunityCandidateConfig(selectedScope);

  // Derive values from config or local edits
  const threshold = localThreshold ?? config?.threshold ?? 7;
  const scoringSignals = localSignals ?? config?.signals ?? [];

  // Calculate last run stats from signals data
  const lastRunStats = useMemo(() => {
    if (!signals || signals.length === 0) return null;
    
    const candidateSignals = signals.filter(s => s.candidate_for_processing);
    const scoredSignals = signals.filter(s => s.candidate_score !== null);
    
    // Count signal fires from breakdowns
    const signalFires: Record<string, number> = {};
    signals.forEach(s => {
      if (s.candidate_score_breakdown && Array.isArray(s.candidate_score_breakdown)) {
        (s.candidate_score_breakdown as any[]).forEach(breakdown => {
          if (breakdown.fired) {
            signalFires[breakdown.signal_id] = (signalFires[breakdown.signal_id] || 0) + 1;
          }
        });
      }
    });
    
    const topSignals = Object.entries(signalFires)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([id, count]) => ({ signal_id: id, fire_count: count }));

    return {
      items_scanned: scoredSignals.length,
      candidates_flagged: candidateSignals.length,
      candidate_rate: scoredSignals.length > 0 
        ? (candidateSignals.length / scoredSignals.length * 100).toFixed(1) 
        : '0',
      avg_score: scoredSignals.length > 0
        ? (scoredSignals.reduce((sum, s) => sum + (s.candidate_score || 0), 0) / scoredSignals.length).toFixed(1)
        : '0',
      top_signals: topSignals
    };
  }, [signals]);

  // Calculate signal stats for purge dialog
  const signalStats = useMemo(() => {
    if (!signals || signals.length === 0) {
      return { total: 0, oldest: null, newest: null, byStatus: {} };
    }
    
    const sorted = [...signals].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    
    const byStatus: Record<string, number> = {};
    signals.forEach(s => {
      byStatus[s.status] = (byStatus[s.status] || 0) + 1;
    });
    
    return {
      total: signals.length,
      oldest: sorted[0]?.created_at || null,
      newest: sorted[sorted.length - 1]?.created_at || null,
      byStatus
    };
  }, [signals]);

  const handleThresholdChange = (value: number) => {
    setLocalThreshold(value);
    setHasChanges(true);
  };

  const handleSignalsChange = (newSignals: CommunitySignal[]) => {
    setLocalSignals(newSignals);
    setHasChanges(true);
  };

  const handleSaveChanges = () => {
    if (localThreshold !== null) {
      updateThreshold(localThreshold);
    }
    if (localSignals !== null) {
      updateSignals(localSignals);
    }
    setHasChanges(false);
    setLocalThreshold(null);
    setLocalSignals(null);
  };

  const handleScopeChange = (scope: CommunitySourceScope) => {
    setLocalThreshold(null);
    setLocalSignals(null);
    setHasChanges(false);
    setSelectedScope(scope);
  };

  const formatLastRun = (timestamp: string | null) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    return date.toLocaleString("en-CA", {
      timeZone: "America/Toronto",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="secondary">Unknown</Badge>;
    switch (status) {
      case "RUNNING":
        return <Badge className="bg-blue-500">Running</Badge>;
      case "SUCCESS":
        return <Badge className="bg-green-500">Success</Badge>;
      case "FAILED":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCadenceLabel = (cadence: string | null) => {
    switch (cadence) {
      case "H12": return "Every 12h";
      case "H24": return "Every 24h";
      case "D7": return "Every 7d";
      case "NONE": return "Disabled";
      case "ON_BATCH_RUN": return "On batch";
      default: return cadence || "Unknown";
    }
  };

  return (
    <div className="space-y-4">
      {/* Section 1: Ingestion Control Bar - Always visible (not collapsible) */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              {getStatusBadge(settings?.last_run_status ?? null)}
              <span className="text-sm text-muted-foreground">
                {getCadenceLabel(settings?.cadence ?? null)}
              </span>
              <Separator orientation="vertical" className="h-5 hidden sm:block" />
              <span className="text-sm text-muted-foreground">
                Last run: {formatLastRun(settings?.last_run_at ?? null)}
              </span>
              {settings?.last_run_error && (
                <Badge variant="destructive" className="text-xs">
                  Error: {settings.last_run_error}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{signals?.length || 0} signals</Badge>
              <Button
                onClick={() => runIngestion()}
                disabled={isRunning}
                size="sm"
                className="gap-2"
              >
                {isRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Run Now
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetchAll()}
                disabled={isLoadingSettings || isLoadingSources || isLoadingSignals}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Ingested Signals - Collapsible (default collapsed) */}
      <CollapsibleSection
        title="Ingested Signals"
        description="View collected forum posts"
        icon={<List className="h-5 w-5" />}
        badge={<Badge variant="secondary">{signals?.length || 0}</Badge>}
      >
        <div className="space-y-4">
          {/* Purge Controls */}
          {signalStats.total > 0 && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="text-sm text-muted-foreground">
                {signalStats.total} signals stored
                {signalStats.oldest && (
                  <span className="ml-2">
                    • Range: {new Date(signalStats.oldest).toLocaleDateString()} – {new Date(signalStats.newest!).toLocaleDateString()}
                  </span>
                )}
              </div>
              <PurgeSignalsDialog 
                stats={signalStats} 
                onPurgeComplete={() => refetchAll()}
              />
            </div>
          )}
          
          <CommunitySignalsList 
            signals={signals || []} 
            isLoading={isLoadingSignals} 
          />
        </div>
      </CollapsibleSection>

      {/* Section 3: Scoring Configuration - Collapsible (default collapsed) */}
      <CollapsibleSection
        title="Scoring Configuration"
        description="Configure detection signals and their weights"
        icon={<Settings2 className="h-5 w-5" />}
        badge={
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {scoringSignals.filter(s => s.enabled).length} active signals
            </Badge>
            {hasChanges && (
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                unsaved
              </Badge>
            )}
          </div>
        }
      >
        <div className="space-y-6">
          {/* Detection Summary (compact) */}
          <CommunityCandidateSummary 
            config={config ? { ...config, threshold, signals: scoringSignals } : null}
            isLoading={isLoadingConfig}
            lastRunStats={lastRunStats}
          />

          <Separator />

          {/* Scoring Controls */}
          <div className="flex items-center gap-6 p-3 bg-muted/50 rounded-lg flex-wrap">
            {config && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={config.enabled}
                  onCheckedChange={toggleEnabled}
                  disabled={isLoadingConfig || isSaving}
                />
                <Label className="text-sm">Scoring {config.enabled ? 'Enabled' : 'Disabled'}</Label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Threshold:</Label>
              <Input
                type="number"
                value={threshold}
                onChange={(e) => handleThresholdChange(parseFloat(e.target.value) || 0)}
                className="w-20 h-8"
                disabled={isSaving}
              />
            </div>
            {hasChanges && (
              <Button 
                onClick={handleSaveChanges}
                disabled={isSaving}
                size="sm"
                className="gap-2 ml-auto"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Changes
              </Button>
            )}
          </div>

          {/* Tabs for Signals vs Weights */}
          <Tabs defaultValue="signals" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signals">Detection Signals</TabsTrigger>
              <TabsTrigger value="weights">Ingestion Weights</TabsTrigger>
            </TabsList>
            <TabsContent value="signals" className="mt-4">
              <CommunitySignalBuilder
                signals={scoringSignals}
                onChange={handleSignalsChange}
                disabled={isSaving}
              />
            </TabsContent>
            <TabsContent value="weights" className="mt-4">
              <SignalScoreConfigPanel />
            </TabsContent>
          </Tabs>
        </div>
      </CollapsibleSection>

      {/* Section 4: Per-Source Configuration - Collapsible (default collapsed) */}
      <CollapsibleSection
        title="Per-Source Configuration"
        description="Scope switching with clickable cards"
        icon={<Layers className="h-5 w-5" />}
        badge={<Badge variant="outline">{selectedScope}</Badge>}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label>Config scope:</Label>
            <Select
              value={selectedScope}
              onValueChange={(v) => handleScopeChange(v as CommunitySourceScope)}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {allConfigs?.map(cfg => (
              <div 
                key={cfg.scope}
                className={cn(
                  "p-3 rounded-lg border cursor-pointer transition-colors",
                  cfg.scope === selectedScope 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-muted-foreground/30'
                )}
                onClick={() => handleScopeChange(cfg.scope)}
              >
                <div className="flex items-center gap-2 mb-1">
                  {cfg.enabled ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <AlertCircle className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className="text-xs font-medium">{cfg.scope}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {cfg.signals.filter(s => s.enabled).length} signals | T:{cfg.threshold}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 5: Ingestion Settings - Collapsible (default collapsed) */}
      <CollapsibleSection
        title="Ingestion Settings"
        description="Source management and configuration"
        icon={<Settings className="h-5 w-5" />}
      >
        <CommunitySignalsSettings
          settings={settings}
          sources={sources || []}
          isLoadingSettings={isLoadingSettings}
          isLoadingSources={isLoadingSources}
        />
      </CollapsibleSection>
    </div>
  );
}
